/**
 * Alshallal Pastry Company HR System - Permission Context
 * Phase 5: Frontend UI Gating based on granular permissions
 * 
 * Fetches user permissions from /api/rbac/me/permissions and provides:
 * - Permission caching in state (refreshed on login/token refresh)
 * - hasPermission() check for UI gating
 * - Permission loading state
 * 
 * UI gating is for UX only - backend guards remain the enforcement layer.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PermissionContext = createContext(null);

// Permission constants for easy reference
export const Permission = {
  // Employee Module
  EMPLOYEE_VIEW_OWN: 'employee_view_own',
  EMPLOYEE_VIEW_ALL: 'employee_view_all',
  EMPLOYEE_CREATE: 'employee_create',
  EMPLOYEE_EDIT: 'employee_edit',
  EMPLOYEE_DELETE: 'employee_delete',
  EMPLOYEE_EXPORT: 'employee_export',
  EMPLOYEE_BULK_IMPORT: 'employee_bulk_import',
  EMPLOYEE_VIEW_SENSITIVE: 'employee_view_sensitive',
  
  // Leave Module
  LEAVE_VIEW_OWN: 'leave_view_own',
  LEAVE_VIEW_TEAM: 'leave_view_team',
  LEAVE_VIEW_ALL: 'leave_view_all',
  LEAVE_REQUEST: 'leave_request',
  LEAVE_APPROVE_MANAGER: 'leave_approve_manager',
  LEAVE_APPROVE_HR: 'leave_approve_hr',
  LEAVE_REJECT: 'leave_reject',
  LEAVE_CANCEL: 'leave_cancel',
  LEAVE_TYPES_MANAGE: 'leave_types_manage',
  LEAVE_BALANCE_ADJUST: 'leave_balance_adjust',
  LEAVE_EXPORT: 'leave_export',
  
  // Request Module
  REQUEST_VIEW_OWN: 'request_view_own',
  REQUEST_VIEW_TEAM: 'request_view_team',
  REQUEST_VIEW_ALL: 'request_view_all',
  REQUEST_CREATE: 'request_create',
  REQUEST_APPROVE_MANAGER: 'request_approve_manager',
  REQUEST_APPROVE_HR: 'request_approve_hr',
  REQUEST_REJECT: 'request_reject',
  REQUEST_CANCEL: 'request_cancel',
  REQUEST_VOID: 'request_void',
  REQUEST_HR_OVERRIDE: 'request_hr_override',
  REQUEST_EXPORT: 'request_export',
  REQUEST_TYPES_MANAGE: 'request_types_manage',
  
  // Letters Module
  LETTERS_VIEW_OWN: 'letters_view_own',
  LETTERS_VIEW_ALL: 'letters_view_all',
  LETTERS_REQUEST: 'letters_request',
  LETTERS_APPROVE: 'letters_approve',
  LETTERS_REJECT: 'letters_reject',
  LETTERS_ISSUE: 'letters_issue',
  LETTERS_TEMPLATE_CREATE: 'letters_template_create',
  LETTERS_TEMPLATE_EDIT: 'letters_template_edit',
  LETTERS_TEMPLATE_PUBLISH: 'letters_template_publish',
  LETTERS_TEMPLATE_DELETE: 'letters_template_delete',
  
  // Document Module
  DOCUMENT_VIEW_OWN: 'document_view_own',
  DOCUMENT_VIEW_ALL: 'document_view_all',
  DOCUMENT_UPLOAD_OWN: 'document_upload_own',
  DOCUMENT_UPLOAD_FOR_EMPLOYEE: 'document_upload_for_employee',
  DOCUMENT_VERIFY: 'document_verify',
  DOCUMENT_REJECT: 'document_reject',
  DOCUMENT_DELETE: 'document_delete',
  DOCUMENT_ARCHIVE: 'document_archive',
  DOCUMENT_EXPORT: 'document_export',
  COMPANY_DOCUMENT_VIEW: 'company_document_view',
  COMPANY_DOCUMENT_MANAGE: 'company_document_manage',
  
  // User Management
  USER_VIEW_ALL: 'user_view_all',
  USER_CREATE: 'user_create',
  USER_EDIT: 'user_edit',
  USER_DELETE: 'user_delete',
  USER_DEACTIVATE: 'user_deactivate',
  USER_ARCHIVE: 'user_archive',
  USER_ROLE_ASSIGN: 'user_role_assign',
  
  // Audit Module
  AUDIT_VIEW_OWN: 'audit_view_own',
  AUDIT_VIEW_ALL: 'audit_view_all',
  AUDIT_EXPORT: 'audit_export',
  
  // System Module
  SYSTEM_SETTINGS: 'system_settings',
  SYSTEM_INTEGRATIONS: 'system_integrations',
  SYSTEM_COMPANY_SETTINGS: 'system_company_settings',
  
  // Dashboard & Reports
  DASHBOARD_EMPLOYEE: 'dashboard_employee',
  DASHBOARD_MANAGER: 'dashboard_manager',
  DASHBOARD_HR: 'dashboard_hr',
  REPORTS_VIEW_ALL: 'reports_view_all',
  
  // Special Permissions
  HR_BYPASS: 'hr_bypass',
  SUPER_ADMIN_OVERRIDE: 'super_admin_override',
  
  // Authority Dashboard
  AUTHORITY_DASHBOARD_VIEW: 'authority_dashboard_view',
};

// Permission to sidebar mapping
export const SIDEBAR_PERMISSION_MAP = {
  'dashboard': [Permission.DASHBOARD_EMPLOYEE, Permission.DASHBOARD_MANAGER, Permission.DASHBOARD_HR],
  'my-profile': null, // All authenticated users
  'employees': [Permission.EMPLOYEE_VIEW_ALL],
  'documents-company': [Permission.COMPANY_DOCUMENT_VIEW, Permission.COMPANY_DOCUMENT_MANAGE],
  'leave': [Permission.LEAVE_VIEW_OWN, Permission.LEAVE_REQUEST],
  'leave-approvals': [Permission.LEAVE_APPROVE_MANAGER, Permission.LEAVE_APPROVE_HR],
  'leave-admin': [Permission.LEAVE_VIEW_ALL, Permission.LEAVE_TYPES_MANAGE],
  'requests': [Permission.REQUEST_VIEW_OWN, Permission.REQUEST_CREATE],
  'requests-approvals': [Permission.REQUEST_APPROVE_MANAGER, Permission.REQUEST_APPROVE_HR],
  'requests-admin': [Permission.REQUEST_VIEW_ALL],
  'letters': [Permission.LETTERS_VIEW_OWN, Permission.LETTERS_REQUEST],
  'letters-hr': [Permission.LETTERS_VIEW_ALL],
  'import': [Permission.EMPLOYEE_BULK_IMPORT],
  'org-chart': [Permission.EMPLOYEE_VIEW_ALL],
  'system': [Permission.AUDIT_VIEW_ALL, Permission.SYSTEM_SETTINGS],
  'settings': [Permission.SYSTEM_SETTINGS, Permission.USER_VIEW_ALL],
  'authority': [Permission.AUTHORITY_DASHBOARD_VIEW],
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Fetch permissions from backend
  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setPermissions([]);
      setPermissionsLoading(false);
      return;
    }

    // The login mode is still unanswered, so this route - like every other one -
    // is refused with 403. Asking anyway would cache that refusal as "no
    // permissions at all", which is exactly what left the sidebar showing two
    // items after the choice was made. The chooser is what is on screen at this
    // point, so there is nothing to gate yet.
    if (user?.login_mode_required) {
      return;
    }

    try {
      setPermissionsLoading(true);
      setPermissionsError(null);
      
      const response = await axios.get(`${API_URL}/rbac/me/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const perms = response.data?.permissions || [];
      setPermissions(perms);
      setLastFetched(new Date());
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setPermissionsError(err.response?.data?.detail?.error || 'Failed to load permissions');
      // Fallback: empty permissions (deny-by-default in UI)
      setPermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  }, [isAuthenticated, token, user?.login_mode_required]);

  // Fetch permissions on auth change - and on a mode change, because entering
  // as Employee or stepping back up into a role changes the effective role the
  // server answers with, and therefore every gated item in the menu.
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions, user?.id, user?.role, user?.login_mode]);

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} - True if user has permission
   */
  const hasPermission = useCallback((permission) => {
    if (!permission) return true; // No permission required
    
    // Super admin always has all permissions
    if (user?.role === 'super_admin') return true;
    
    return permissions.includes(permission);
  }, [permissions, user?.role]);

  /**
   * Check if user has any of the given permissions
   * @param {string[]} perms - Array of permissions to check
   * @returns {boolean} - True if user has at least one permission
   */
  const hasAnyPermission = useCallback((perms) => {
    if (!perms || perms.length === 0) return true;
    
    // Super admin always has all permissions
    if (user?.role === 'super_admin') return true;
    
    return perms.some(p => permissions.includes(p));
  }, [permissions, user?.role]);

  /**
   * Check if user has all of the given permissions
   * @param {string[]} perms - Array of permissions to check
   * @returns {boolean} - True if user has all permissions
   */
  const hasAllPermissions = useCallback((perms) => {
    if (!perms || perms.length === 0) return true;
    
    // Super admin always has all permissions
    if (user?.role === 'super_admin') return true;
    
    return perms.every(p => permissions.includes(p));
  }, [permissions, user?.role]);

  /**
   * Check if a sidebar item should be visible
   * @param {string} itemKey - Sidebar item key
   * @returns {boolean} - True if item should be visible
   */
  const canViewSidebarItem = useCallback((itemKey) => {
    const requiredPerms = SIDEBAR_PERMISSION_MAP[itemKey];
    
    // No permission required (null) - visible to all
    if (requiredPerms === null || requiredPerms === undefined) return true;
    
    return hasAnyPermission(requiredPerms);
  }, [hasAnyPermission]);

  /**
   * Check if user is HR staff (has any HR-specific permissions)
   * @returns {boolean}
   */
  const isHRStaff = useCallback(() => {
    const hrPermissions = [
      Permission.EMPLOYEE_VIEW_ALL,
      Permission.LEAVE_VIEW_ALL,
      Permission.REQUEST_VIEW_ALL,
      Permission.LETTERS_VIEW_ALL,
      Permission.HR_BYPASS,
    ];
    return hasAnyPermission(hrPermissions);
  }, [hasAnyPermission]);

  /**
   * Check if user can manage users
   * @returns {boolean}
   */
  const canManageUsers = useCallback(() => {
    return hasAnyPermission([Permission.USER_VIEW_ALL, Permission.USER_CREATE, Permission.USER_EDIT]);
  }, [hasAnyPermission]);

  /**
   * Check if user can approve leaves
   * @returns {boolean}
   */
  const canApproveLeave = useCallback(() => {
    return hasAnyPermission([Permission.LEAVE_APPROVE_MANAGER, Permission.LEAVE_APPROVE_HR]);
  }, [hasAnyPermission]);

  /**
   * Check if user can approve requests
   * @returns {boolean}
   */
  const canApproveRequests = useCallback(() => {
    return hasAnyPermission([Permission.REQUEST_APPROVE_MANAGER, Permission.REQUEST_APPROVE_HR]);
  }, [hasAnyPermission]);

  /**
   * Check if user can export data
   * @param {string} module - Module name (employee, leave, request, document, audit)
   * @returns {boolean}
   */
  const canExport = useCallback((module) => {
    const exportPerms = {
      employee: Permission.EMPLOYEE_EXPORT,
      leave: Permission.LEAVE_EXPORT,
      request: Permission.REQUEST_EXPORT,
      document: Permission.DOCUMENT_EXPORT,
      audit: Permission.AUDIT_EXPORT,
    };
    return hasPermission(exportPerms[module] || `${module}_export`);
  }, [hasPermission]);

  const value = {
    permissions,
    permissionsLoading,
    permissionsError,
    lastFetched,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canViewSidebarItem,
    isHRStaff,
    canManageUsers,
    canApproveLeave,
    canApproveRequests,
    canExport,
    refreshPermissions: fetchPermissions,
    Permission, // Export constants for easy access
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;
